/* =============================================================================
   SUPPLY: theme.js (global). No dependencies. Custom elements + small helpers.
   ============================================================================= */
(function () {
  'use strict';

  const theme = window.theme || {};
  const routes = theme.routes || {};
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- helpers ---------- */
  const debounce = (fn, wait = 200) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(null, args), wait); };
  };

  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const fmt = format || theme.moneyFormat || '${{amount}}';
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = fmt.match(placeholderRegex);
    const type = match ? match[1] : 'amount';
    function formatWithDelimiters(number, precision = 2, thousands = ',', decimal = '.') {
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      const parts = number.split('.');
      const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      const c = parts[1] ? decimal + parts[1] : '';
      return dollars + c;
    }
    let value = '';
    switch (type) {
      case 'amount': value = formatWithDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = formatWithDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = formatWithDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = formatWithDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = formatWithDelimiters(cents, 2, "'", '.'); break;
      default: value = formatWithDelimiters(cents, 2);
    }
    return fmt.replace(placeholderRegex, value);
  }

  const fetchConfig = (type = 'json') => ({
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
  });

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function toast(message, { link, linkLabel, timeout = 3500, icon = 'check' } = {}) {
    const root = document.getElementById('ToastRoot');
    if (!root) return;
    const el = document.createElement('div');
    el.className = 'toast';
    el.setAttribute('role', 'status');
    const icons = {
      check: '<svg class="icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m5 12 5 5L20 7"/></svg>',
      error: '<svg class="icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v5M12 16h.01"/></svg>'
    };
    el.innerHTML = `${icons[icon] || ''}<span>${message}</span>${link ? `<a href="${link}">${linkLabel || ''}</a>` : ''}`;
    root.appendChild(el);
    setTimeout(() => { el.style.opacity = '0'; el.style.transition = 'opacity .3s'; setTimeout(() => el.remove(), 300); }, timeout);
  }

  window.theme = Object.assign(theme, { formatMoney, toast, debounce, parseHTML, fetchConfig });

  /* ---------- Drawers & modals (<dialog>) ---------- */
  const Drawer = {
    open(dialog, opener) {
      if (!dialog || dialog.open) return;
      dialog.__opener = opener || document.activeElement;
      dialog.showModal();
      document.body.classList.add('is-locked');
      requestAnimationFrame(() => requestAnimationFrame(() => dialog.classList.add('is-open')));
      const focusable = dialog.querySelector('[autofocus], input:not([type=hidden]), button:not([data-close-drawer]), a[href]');
      if (focusable && dialog.matches('.search-drawer')) setTimeout(() => focusable.focus(), 50);
      dialog.dispatchEvent(new CustomEvent('drawer:open', { bubbles: true }));
    },
    close(dialog) {
      if (!dialog || !dialog.open) return;
      dialog.classList.remove('is-open');
      const done = () => {
        if (dialog.open) dialog.close();
        if (!document.querySelector('dialog[open]')) document.body.classList.remove('is-locked');
        dialog.dispatchEvent(new CustomEvent('drawer:close', { bubbles: true }));
        if (dialog.__opener && typeof dialog.__opener.focus === 'function') dialog.__opener.focus();
      };
      if (reducedMotion) done(); else setTimeout(done, 380);
    },
    toggle(id, opener) {
      const dialog = document.getElementById(id);
      if (!dialog) return;
      dialog.open ? this.close(dialog) : this.open(dialog, opener);
    }
  };
  window.theme.Drawer = Drawer;

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-open-drawer]');
    if (opener) {
      const id = opener.getAttribute('data-open-drawer');
      if (document.getElementById(id)) { e.preventDefault(); Drawer.open(document.getElementById(id), opener); }
      return;
    }
    const closer = e.target.closest('[data-close-drawer]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) { e.preventDefault(); Drawer.close(dialog); }
    }
  });
  document.addEventListener('cancel', (e) => {
    if (e.target.tagName === 'DIALOG') { e.preventDefault(); Drawer.close(e.target); }
  }, true);
  // Click on the dialog backdrop area (outside the panel) closes it
  document.addEventListener('click', (e) => {
    if (e.target.tagName === 'DIALOG' && e.target.classList.contains('modal')) Drawer.close(e.target);
  });

  /* ---------- Header ---------- */
  class HeaderComponent extends HTMLElement {
    connectedCallback() {
      this.wrapper = this.closest('.shopify-section');
      this.sticky = this.dataset.sticky === 'true';
      this.transparent = this.dataset.transparent === 'true';
      if (this.wrapper) {
        this.wrapper.classList.add('is-sticky-header');
        if (!this.sticky) this.wrapper.style.position = 'relative';
      }
      this.setHeight();
      window.addEventListener('resize', debounce(() => this.setHeight(), 150));
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
      this.initHoverMenus();
    }
    setHeight() {
      const h = Math.round(this.getBoundingClientRect().height) || 72;
      document.documentElement.style.setProperty('--header-height', `${h}px`);
      const bar = document.querySelector('[data-announcement-bar]');
      document.documentElement.style.setProperty('--announcement-height', `${bar ? bar.offsetHeight : 0}px`);
    }
    onScroll() {
      const scrolled = window.scrollY > 24;
      if (scrolled !== this.wasScrolled) {
        this.wasScrolled = scrolled;
        document.documentElement.classList.toggle('is-scrolled', scrolled);
        if (this.transparent) this.classList.toggle('site-header--transparent', !scrolled && !this.menuOpen);
      }
    }
    initHoverMenus() {
      const items = this.querySelectorAll('[data-hover-menu]');
      const canHover = window.matchMedia('(hover: hover)').matches;
      items.forEach((details) => {
        const summary = details.querySelector('summary');
        let closeTimer;
        const open = () => { clearTimeout(closeTimer); items.forEach((d) => { if (d !== details) d.removeAttribute('open'); }); details.setAttribute('open', ''); this.setMenuOpen(true); };
        const close = () => { closeTimer = setTimeout(() => { details.removeAttribute('open'); this.setMenuOpen(!!this.querySelector('[data-hover-menu][open]')); }, 120); };
        // data-click-only panels (All categories) open on click, never on hover.
        const clickOnly = details.hasAttribute('data-click-only');
        if (canHover && !clickOnly) {
          details.addEventListener('mouseenter', open);
          details.addEventListener('mouseleave', close);
        }
        if (canHover || clickOnly) {
          summary.addEventListener('click', (e) => { e.preventDefault(); details.hasAttribute('open') ? (details.removeAttribute('open'), this.setMenuOpen(false)) : open(); });
        } else {
          summary.addEventListener('click', () => { items.forEach((d) => { if (d !== details) d.removeAttribute('open'); }); setTimeout(() => this.setMenuOpen(details.hasAttribute('open')), 0); });
        }
        details.addEventListener('keydown', (e) => { if (e.key === 'Escape') { details.removeAttribute('open'); summary.focus(); this.setMenuOpen(false); } });
        details.addEventListener('focusout', (e) => { if (!details.contains(e.relatedTarget)) { details.removeAttribute('open'); this.setMenuOpen(false); } });
      });
      document.addEventListener('click', (e) => {
        if (!this.contains(e.target)) { items.forEach((d) => d.removeAttribute('open')); this.setMenuOpen(false); }
      });
    }
    setMenuOpen(state) {
      this.menuOpen = state;
      if (this.transparent) this.classList.toggle('site-header--transparent', !state && !this.wasScrolled);
    }
  }
  customElements.define('header-component', HeaderComponent);

  /* ---------- Announcement bar rotation (mobile) ---------- */
  (function () {
    const bar = document.querySelector('[data-announcement-bar][data-rotate]');
    if (!bar) return;
    const messages = bar.querySelectorAll('.announcement-bar__message');
    if (messages.length < 2) return;
    let i = 0;
    setInterval(() => {
      if (window.innerWidth >= 1024) return;
      messages[i].classList.remove('is-active');
      i = (i + 1) % messages.length;
      messages[i].classList.add('is-active');
    }, 4000);
  })();

  /* ---------- Quantity input ---------- */
  class QuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector('input');
      this.querySelector('[data-qty-minus]')?.addEventListener('click', () => this.step(-1));
      this.querySelector('[data-qty-plus]')?.addEventListener('click', () => this.step(1));
      this.input?.addEventListener('change', () => this.validate());
      this.validate();
    }
    step(dir) {
      const min = parseInt(this.input.min || '0', 10);
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      let v = parseInt(this.input.value || '0', 10) + dir;
      v = Math.max(min, Math.min(max, v));
      this.input.value = v;
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
      this.validate();
    }
    validate() {
      if (!this.input) return;
      const min = parseInt(this.input.min || '0', 10);
      const max = this.input.max ? parseInt(this.input.max, 10) : Infinity;
      const v = parseInt(this.input.value || '0', 10);
      const minus = this.querySelector('[data-qty-minus]');
      const plus = this.querySelector('[data-qty-plus]');
      if (minus) minus.disabled = v <= min && min > 0;
      if (plus) plus.disabled = v >= max;
    }
  }
  customElements.define('quantity-input', QuantityInput);

  /* ---------- Cart API ---------- */
  const Cart = {
    async add(formData) {
      formData.append('sections', 'cart-drawer');
      formData.append('sections_url', window.location.pathname);
      const res = await fetch(`${routes.cartAdd}.js`, { method: 'POST', headers: { Accept: 'application/javascript' }, body: formData });
      const data = await res.json();
      if (!res.ok || data.status) throw data;
      return data;
    },
    async change(payload, sections) {
      const body = Object.assign({ sections, sections_url: window.location.pathname }, payload);
      const res = await fetch(`${routes.cartChange}.js`, { ...fetchConfig(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw data;
      return data;
    },
    async update(payload, sections) {
      const body = Object.assign({ sections, sections_url: window.location.pathname }, payload);
      const res = await fetch(`${routes.cartUpdate}.js`, { ...fetchConfig(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) throw data;
      return data;
    },
    setCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach((el) => { el.textContent = count; el.dataset.count = count; });
    },
    async refreshDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;
      const res = await fetch(`${window.location.pathname}?sections=cart-drawer`);
      const data = await res.json();
      drawer.renderContents(data['cart-drawer']);
    }
  };
  window.theme.Cart = Cart;

  class CartDrawer extends HTMLElement {
    renderContents(html) {
      if (!html) return;
      const doc = parseHTML(html);
      const newPanel = doc.querySelector('#CartDrawerPanel');
      const panel = this.querySelector('#CartDrawerPanel');
      if (newPanel && panel) panel.innerHTML = newPanel.innerHTML;
      const countEl = doc.querySelector('cart-drawer');
      if (countEl && countEl.dataset.itemCount != null) Cart.setCount(countEl.dataset.itemCount);
      this.classList.remove('is-loading');
    }
    open(opener) { Drawer.open(this.querySelector('dialog'), opener); }
  }
  customElements.define('cart-drawer', CartDrawer);

  class CartItems extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.addEventListener('change', debounce((e) => {
        const input = e.target.closest('[data-qty-input]');
        if (input) this.updateQuantity(input.dataset.index, input.value, input);
      }, 250));
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-remove-item]');
        if (btn) { e.preventDefault(); this.updateQuantity(btn.dataset.index, 0, btn); }
      });
    }
    async updateQuantity(line, quantity, trigger) {
      const wrapper = this.closest('cart-drawer') || this.closest('.shopify-section') || this;
      wrapper.classList.add('is-loading');
      const item = trigger.closest('[data-cart-item]');
      try {
        const data = await Cart.change({ line, quantity }, [this.sectionId]);
        Cart.setCount(data.item_count);
        const html = data.sections && data.sections[this.sectionId];
        if (this.sectionId === 'cart-drawer') {
          document.querySelector('cart-drawer')?.renderContents(html);
        } else if (html) {
          const section = document.getElementById(`shopify-section-${this.sectionId}`);
          const doc = parseHTML(html);
          const fresh = doc.getElementById(`shopify-section-${this.sectionId}`);
          if (section && fresh) section.innerHTML = fresh.innerHTML;
        }
        // Quantity error (stock limit): Shopify returns the same quantity as before
        const updated = data.items && data.items[line - 1];
        if (updated && quantity > 0 && updated.quantity < quantity) {
          const err = document.querySelector(`[data-cart-item][data-index="${line}"] [data-item-error]`);
          if (err) { err.hidden = false; err.textContent = (theme.strings.quantityError || '').replace('[quantity]', updated.quantity); }
        }
        document.dispatchEvent(new CustomEvent('cart:updated', { detail: data }));
      } catch (err) {
        const el = item && item.querySelector('[data-item-error]');
        if (el) { el.hidden = false; el.textContent = (err && err.message) || theme.strings.error; }
        else toast(theme.strings.error, { icon: 'error' });
      } finally {
        wrapper.classList.remove('is-loading');
      }
    }
  }
  customElements.define('cart-items', CartItems);

  // Cart note
  document.addEventListener('change', debounce((e) => {
    const note = e.target.closest('[data-cart-note]');
    if (note) Cart.update({ note: note.value }).catch(() => {});
  }, 400));

  /* ---------- Product form (add to cart) ---------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.submitBtn = this.form.querySelector('[type="submit"]');
      this.errorEl = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }
    async onSubmit(e) {
      e.preventDefault();
      if (this.submitBtn?.getAttribute('aria-disabled') === 'true') return;
      this.submitBtn?.classList.add('is-loading');
      this.submitBtn?.setAttribute('aria-disabled', 'true');
      if (this.errorEl) this.errorEl.hidden = true;
      try {
        const formData = new FormData(this.form);
        const data = await Cart.add(formData);
        const drawer = document.querySelector('cart-drawer');
        if (drawer && theme.cartType === 'drawer') {
          drawer.renderContents(data.sections && data.sections['cart-drawer']);
          // close quick view if open
          const qv = document.getElementById('QuickView');
          if (qv && qv.open) Drawer.close(qv);
          drawer.open(this.submitBtn);
          // count from the drawer markup
          const doc = parseHTML(data.sections['cart-drawer']);
          const el = doc.querySelector('cart-drawer');
          if (el) Cart.setCount(el.dataset.itemCount);
        } else {
          const res = await fetch(`${routes.cart}.js`); const cart = await res.json();
          Cart.setCount(cart.item_count);
          toast(theme.strings.added, { link: routes.cart, linkLabel: 'View cart' });
        }
        document.dispatchEvent(new CustomEvent('cart:added', { detail: data }));
      } catch (err) {
        const msg = (err && (err.description || err.message)) || theme.strings.error;
        if (this.errorEl) { this.errorEl.hidden = false; this.errorEl.textContent = msg; }
        else toast(msg, { icon: 'error' });
      } finally {
        this.submitBtn?.classList.remove('is-loading');
        this.submitBtn?.removeAttribute('aria-disabled');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------- Scroll carousel ---------- */
  class ScrollCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.carousel');
      if (!this.track) return;
      this.prev = this.querySelector('[data-carousel-prev]');
      this.next = this.querySelector('[data-carousel-next]');
      this.progress = this.querySelector('.carousel-progress__bar');
      this.prev?.addEventListener('click', () => this.scrollBy(-1));
      this.next?.addEventListener('click', () => this.scrollBy(1));
      this.track.addEventListener('scroll', debounce(() => this.update(), 50), { passive: true });
      window.addEventListener('resize', debounce(() => this.update(), 150));
      this.update();
    }
    scrollBy(dir) {
      const item = this.track.querySelector('.carousel__item');
      // data-step="page" moves by most of the visible width (rows of small chips); otherwise by item width.
      const paged = this.dataset.step === 'page' || !item;
      const step = paged ? this.track.clientWidth * .8 : item.getBoundingClientRect().width + parseFloat(getComputedStyle(this.track).columnGap || getComputedStyle(this.track).gap || 0);
      this.track.scrollBy({ left: dir * step * (!paged && window.innerWidth >= 1024 ? 2 : 1), behavior: reducedMotion ? 'auto' : 'smooth' });
    }
    update() {
      const max = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      if (this.prev) this.prev.disabled = x <= 2;
      if (this.next) this.next.disabled = x >= max - 2;
      this.classList.toggle('is-static', max <= 2);
      if (this.progress) {
        const ratio = this.track.clientWidth / this.track.scrollWidth;
        this.progress.style.setProperty('--progress', `${Math.max(ratio * 100, 8)}%`);
        this.progress.style.transform = `translateX(${(x / Math.max(max, 1)) * (100 / ratio - 100)}%)`;
      }
    }
  }
  customElements.define('scroll-carousel', ScrollCarousel);

  /* ---------- Reveal on scroll ---------- */
  const revealObserver = ('IntersectionObserver' in window && !reducedMotion) ? new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) { entry.target.classList.add('is-visible'); revealObserver.unobserve(entry.target); }
    });
  }, { rootMargin: '0px 0px -5% 0px', threshold: 0.01 }) : null;
  function observeReveals(root = document) {
    root.querySelectorAll('[data-reveal]:not(.is-visible)').forEach((el) => {
      if (revealObserver) revealObserver.observe(el); else el.classList.add('is-visible');
    });
  }
  observeReveals();
  window.theme.observeReveals = observeReveals;
  // Safety net: nothing should stay hidden if an observer edge case never fires.
  const revealAll = () => document.querySelectorAll('[data-reveal]:not(.is-visible)').forEach((el) => { const r = el.getBoundingClientRect(); if (r.top < window.innerHeight + 200) el.classList.add('is-visible'); });
  window.addEventListener('load', () => { setTimeout(revealAll, 1500); });
  window.addEventListener('scroll', debounce(revealAll, 400), { passive: true });
  new MutationObserver(debounce(() => observeReveals(), 100)).observe(document.body, { childList: true, subtree: true });

  /* ---------- Share button ---------- */
  class ShareButton extends HTMLElement {
    connectedCallback() {
      this.querySelector('button')?.addEventListener('click', async () => {
        const url = this.dataset.url || window.location.href;
        const title = this.dataset.title || document.title;
        if (navigator.share) { try { await navigator.share({ title, url }); } catch (e) { /* cancelled */ } return; }
        try { await navigator.clipboard.writeText(url); toast(theme.strings.copied); } catch (e) { /* noop */ }
      });
    }
  }
  customElements.define('share-button', ShareButton);

  /* ---------- Shopify theme editor events ---------- */
  document.addEventListener('shopify:section:load', (e) => { observeReveals(e.target); });
  document.addEventListener('shopify:block:select', (e) => {
    const drawer = e.target.closest('dialog');
    if (drawer) Drawer.open(drawer);
  });

  /* ---------- Localization / misc ---------- */
  document.querySelectorAll('.no-js').forEach((el) => el.classList.remove('no-js'));
})();
